import React, { useEffect, useState } from "react";
import { api, fmtCurrency, STAGES, STAGE_COLORS } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, PencilSimple, UsersThree, ArrowsClockwise, FileArrowDown, PaperPlaneTilt, XCircle, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const VENDORS = ["Cisco", "Fortinet", "Palo Alto", "F5", "Checkpoint"];

// ---- PSDoc status helpers (see routes/integration_psdoc.py) ----
const PSDOC_LABELS = {
  sent: "Handoff sent",
  sow_started: "SOW in flight",
  sow_ready: "SOW ready",
  sow_out_for_signing: "Out for signing",
  sow_viewed_by_client: "Client viewed SOW",
  won_ack: "Won → PSDoc",
  closed_lost: "Deal lost",
  reactivated: "Reactivated",
  config_started: "Config started",
  config_passed: "Config passed",
  config_failed: "Config FAILED",
  deployment_ready: "Deployment ready",
  send_failed: "Send failed",
  won_failed: "Won-notify failed",
  skipped_not_configured: "PSDoc not configured",
  won_skipped_not_configured: "PSDoc not configured",
};
function psdocLabel(s) { return PSDOC_LABELS[s] || s; }
function psdocBadgeClass(s) {
  if (["config_failed", "send_failed", "won_failed", "closed_lost"].includes(s)) return "bg-red-100 text-red-700 border border-red-200";
  if (["skipped_not_configured", "won_skipped_not_configured"].includes(s)) return "bg-slate-100 text-slate-600 border border-slate-200";
  if (["sow_ready", "won_ack", "config_passed", "deployment_ready", "reactivated"].includes(s)) return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (["sow_out_for_signing", "sow_viewed_by_client"].includes(s)) return "bg-violet-100 text-violet-700 border border-violet-200";
  return "bg-blue-100 text-blue-700 border border-blue-200";
}

const LOST_CATEGORIES = [
  { key: "budget",       label: "Budget constraints" },
  { key: "board",        label: "Board / management declined" },
  { key: "scope",        label: "Scope changed / no longer needed" },
  { key: "competitor",   label: "Chose a competitor" },
  { key: "timing",       label: "Timing — deferred" },
  { key: "no_response",  label: "No response from client" },
  { key: "other",        label: "Other (please specify)" },
];
const LOST_LABEL = Object.fromEntries(LOST_CATEGORIES.map(c => [c.key, c.label]));

export default function Pipeline() {
  const [projects, setProjects] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProject, setAssignProject] = useState(null);
  // Mark-as-lost dialog
  const [lostOpen, setLostOpen] = useState(false);
  const [lostFor, setLostFor] = useState(null);
  const [lostForm, setLostForm] = useState({ category: "budget", reason: "" });

  function initialForm() { return { name:"", client:"", vendors:[], stage:"deal", value:0, margin_target:20, start_date:"", end_date:"", description:"" }; }

  const load = async () => {
    // Feb 2026 — unified pipeline: projects (sow_ready+) merged with pre-SOW
    // clients (discovery / scope in progress). The Discovery + Scope-in-Progress
    // swim-lanes get their content from clients until a project row exists.
    const [pj, pe] = await Promise.all([api.get("/pipeline/entries"), api.get("/personnel")]);
    setProjects(pj.data.entries || []); setPersonnel(pe.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(initialForm()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name:p.name, client:p.client, vendors:p.vendors, stage:p.stage, value:p.value, margin_target:p.margin_target, start_date:p.start_date||"", end_date:p.end_date||"", description:p.description||"" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/projects/${editing.id}`, form);
      else await api.post("/projects", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const openMarkLost = (p) => {
    setLostFor(p);
    setLostForm({ category: "budget", reason: "" });
    setLostOpen(true);
  };
  const submitMarkLost = async () => {
    if (lostForm.category === "other" && !lostForm.reason.trim()) {
      toast.error("Please provide a reason when the category is 'Other'.");
      return;
    }
    try {
      const r = await api.post(`/projects/${lostFor.id}/mark-lost`, lostForm);
      const push = r.data?.psdoc_push || {};
      if (push.ok && !push.skipped) toast.success("Marked as Lost — PSDoc updated");
      else if (push.skipped) toast.warning("Marked as Lost — PSDoc not configured");
      else toast.warning(`Marked as Lost locally — PSDoc mirror failed: ${push.error || "unknown"}`);
      setLostOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark as Lost failed");
    }
  };
  const reactivate = async (p) => {
    if (!window.confirm(`Reactivate "${p.name}"?\n\nThis restores its previous stage (${p.lost_prev_stage || "deal"}) and notifies PSDoc.`)) return;
    try {
      const r = await api.post(`/projects/${p.id}/reactivate`);
      toast.success(`Reactivated → ${r.data.stage}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reactivate failed");
    }
  };
  const toggleVendor = (v) => setForm((f) => ({ ...f, vendors: f.vendors.includes(v) ? f.vendors.filter((x) => x !== v) : [...f.vendors, v] }));

  const openAssign = (p) => { setAssignProject(p); setAssignOpen(true); };
  const toggleAssign = async (personId) => {
    const current = assignProject.assigned_person_ids || [];
    const next = current.includes(personId) ? current.filter(x => x !== personId) : [...current, personId];
    try {
      const res = await api.put(`/projects/${assignProject.id}`, { assigned_person_ids: next });
      setAssignProject(res.data);
      setProjects((prev) => prev.map(p => p.id === res.data.id ? res.data : p));
      toast.success("Assignment updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <div>
      <PageHeader
        overline="Deal Flow"
        title="Project Pipeline"
        subtitle="Every deployment from first quote to closed-won."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn" onClick={openCreate} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Plus size={14} className="mr-2" /> New Project</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Project Name</Label><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>Client</Label><Input value={form.client} onChange={(e) => setForm({...form, client: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>Stage</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}>
                    <SelectTrigger data-testid="project-stage-select" className="bg-slate-50 border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Deal Value ($)</Label><Input data-testid="project-value-input" type="number" value={form.value} onChange={(e) => setForm({...form, value: parseFloat(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                <div><Label>Margin Target (%)</Label><Input type="number" value={form.margin_target} onChange={(e) => setForm({...form, margin_target: parseFloat(e.target.value) || 0})} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
                <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="bg-slate-50 border-slate-300 mt-1"/></div>
                <div className="col-span-2"><Label>Vendors</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {VENDORS.map((v) => <button key={v} type="button" onClick={() => toggleVendor(v)} className={`px-3 py-1 rounded text-xs font-mono uppercase tracking-wider border ${form.vendors.includes(v) ? "bg-[#FFC107]/20 border-[#FFC107] text-[#0F1E38]" : "border-slate-300 text-slate-500"}`}>{v}</button>)}
                  </div>
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-slate-50 border-slate-300 mt-1" rows={3}/></div>
                {editing && <PsdocPanel opportunityId={editing.id} project={editing} status={editing.psdoc_status} url={editing.psdoc_url} error={editing.psdoc_error} attachments={editing.psdoc_attachments} sections={editing.psdoc_sections} onRetry={async () => { try { await api.post(`/integration/psdoc/opportunities/${editing.id}/retry-handoff`); toast.success("Handoff retried"); const fresh = await api.get(`/projects/${editing.id}`); setEditing(fresh.data); load(); } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }}} onRefresh={async () => { const fresh = await api.get(`/projects/${editing.id}`); setEditing(fresh.data); load(); }} />}
              </div>
              <DialogFooter><Button data-testid="save-project-btn" onClick={save} className="bg-[#FFC107] hover:bg-[#E5A906]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Kanban — horizontal scroll to accommodate the extended stage set */}
      <div className="flex gap-4 overflow-x-auto pb-3">
        {STAGES.map((s) => {
          const items = projects.filter((p) => p.stage === s.key);
          return (
            <div key={s.key} data-testid={`stage-col-${s.key}`} className="border border-slate-200 bg-[#F1F5F9] rounded-lg p-3 min-w-[260px] w-[260px] flex-shrink-0">
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="overline">{s.label}</span>
                <span className="text-xs font-mono text-slate-500">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((p) => (
                  <div key={p.id} data-testid={`${p.kind === "client" ? "pipeline-client-card" : "project-card"}-${p.id}`} className={`tactical-card border ${p.kind === "client" ? "border-dashed border-indigo-300" : "border-slate-200"} bg-white rounded p-3`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-[#0F1E38] leading-snug">{p.name}</div>
                      <div className="flex gap-1">
                        {p.kind === "project" ? (
                          <>
                            <button data-testid={`assign-project-${p.id}`} onClick={() => openAssign(p)} className="text-slate-500 hover:text-emerald-400" style={{transition:"color 0.15s ease"}} title="Assign people"><UsersThree size={14} /></button>
                            <button data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)} className="text-slate-500 hover:text-[#0F1E38]" style={{transition:"color 0.15s ease"}} title="Edit"><PencilSimple size={14} /></button>
                            {p.stage === "closed-lost" ? (
                              <button data-testid={`reactivate-project-${p.id}`} onClick={() => reactivate(p)} className="text-slate-500 hover:text-emerald-600" style={{transition:"color 0.15s ease"}} title="Reactivate this deal"><ArrowUUpLeft size={14} /></button>
                            ) : (
                              <button data-testid={`mark-lost-${p.id}`} onClick={() => openMarkLost(p)} className="text-slate-500 hover:text-red-500" style={{transition:"color 0.15s ease"}} title="Mark as Lost"><XCircle size={14} /></button>
                            )}
                          </>
                        ) : (
                          <a href={`/clients?focus=${p.client_id}`} data-testid={`open-client-${p.client_id}`} className="text-[10px] font-mono uppercase text-indigo-600 hover:underline" title="Open on Clients page — this deal is still in Discovery / Scope">Open ↗</a>
                        )}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 font-mono flex items-center gap-2 flex-wrap">
                      {p.client}
                      {p.ps_code && <span className="text-[10px] text-slate-400">· {p.ps_code}</span>}
                      {p.kind === "client" && <span className="text-[9px] font-medium uppercase px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">pre-sow</span>}
                    </div>
                    {p.kind === "project" && <div className="mt-2 flex flex-wrap gap-1">{(p.vendors || []).map((v) => <VendorTag key={v} vendor={v} />)}</div>}
                    {p.kind === "project" && p.assigned_person_ids?.length > 0 && (
                      <div className="mt-2 text-[10px] font-mono text-emerald-400">
                        <UsersThree size={10} className="inline mr-1" />{p.assigned_person_ids.length} assigned
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STAGE_COLORS[p.stage]}`}>{p.stage}</span>
                      {p.kind === "project"
                        ? <span className="font-mono text-sm text-[#0F1E38]">{fmtCurrency(p.value)}</span>
                        : <span className="font-mono text-[10px] text-slate-500 uppercase" title={p.discovery_status}>{(p.discovery_status || "").replace(/_/g, " ")}</span>}
                    </div>
                    {p.kind === "client" && p.revision_round > 1 && (
                      <div className="mt-2 text-[10px] font-mono bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-0.5 uppercase">
                        Revision · Round {p.revision_round}
                      </div>
                    )}
                    {p.stage === "closed-lost" && p.lost_category && (
                      <div className="mt-2 text-[10px] font-mono bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1" data-testid={`lost-reason-${p.id}`}>
                        <div className="uppercase font-semibold">Lost · {LOST_LABEL[p.lost_category] || p.lost_category}</div>
                        {p.lost_reason && <div className="mt-0.5 whitespace-pre-wrap normal-case">{p.lost_reason}</div>}
                      </div>
                    )}
                    {p.psdoc_status && (
                      <div className="mt-2 text-[10px] flex items-center gap-1.5" title={p.psdoc_error || ""}>
                        <span className="font-mono uppercase text-slate-500">PSDoc</span>
                        <span className={`px-1.5 py-0.5 rounded font-mono uppercase ${psdocBadgeClass(p.psdoc_status)}`}>
                          {psdocLabel(p.psdoc_status)}
                        </span>
                        {p.psdoc_url && (
                          <a href={p.psdoc_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">↗</a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-slate-400 text-center py-6 font-mono">— empty —</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Assignment dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Assign Personnel · {assignProject?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {personnel.map((p) => {
              const assigned = assignProject?.assigned_person_ids?.includes(p.id);
              return (
                <button
                  key={p.id}
                  data-testid={`assign-person-${p.id}`}
                  onClick={() => toggleAssign(p.id)}
                  className={`text-left border rounded-lg p-3 ${assigned ? "border-emerald-500/50 bg-emerald-500/[0.06]" : "border-slate-200 bg-slate-50 hover:border-blue-500/40"}`}
                  style={{transition:"border-color 0.15s ease, background-color 0.15s ease"}}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#0F1E38]">{p.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{p.role_title} · {p.region}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {p.person_type?.map((t) => <span key={t} className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded border w-fit ${t === "engineer" ? "text-[#0F1E38] border-blue-500/40 bg-blue-500/10" : "text-violet-300 border-violet-500/40 bg-violet-500/10"}`}>{t.replace("_", " ")}</span>)}
                    </div>
                  </div>
                  <div className="mt-2 text-[10px] font-mono text-slate-500">
                    Util {p.utilization}% · {p.day_rate > 0 && `$${p.day_rate}/d`}{p.hourly_rate > 0 && ` $${p.hourly_rate}/hr`}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark-as-Lost dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg" data-testid="mark-lost-dialog">
          <DialogHeader><DialogTitle>Mark as Lost · {lostFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              This flips the deal to <span className="font-mono">closed-lost</span> and notifies PSDoc so their board mirrors.
              Reminder emails for this project's milestones will be paused. You can reactivate any time.
            </p>
            <div>
              <Label className="text-xs">Reason category</Label>
              <Select value={lostForm.category} onValueChange={(v) => setLostForm({ ...lostForm, category: v })}>
                <SelectTrigger className="mt-1" data-testid="lost-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOST_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Detail {lostForm.category === "other" && <span className="text-red-500">(required)</span>}</Label>
              <Textarea
                rows={3}
                value={lostForm.reason}
                onChange={(e) => setLostForm({ ...lostForm, reason: e.target.value })}
                placeholder="e.g. Board approved half the budget; they'll revisit in Q3 FY26."
                className="mt-1"
                data-testid="lost-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostOpen(false)} className="border-slate-300">Cancel</Button>
            <Button onClick={submitMarkLost} data-testid="lost-submit" className="bg-red-600 hover:bg-red-700 text-white">Mark as Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---- PSDoc integration panel (embedded in the edit dialog) ----
function PsdocPanel({ opportunityId, project = {}, status, url, error, attachments = [], sections, onRetry, onRefresh }) {
  const [events, setEvents] = React.useState([]);
  const [expanded, setExpanded] = React.useState(false);
  const [issuing, setIssuing] = React.useState(false);
  const [marking, setMarking] = React.useState(false);
  const [signingConfirmOpen, setSigningConfirmOpen] = React.useState(false);
  const [signForm, setSignForm] = React.useState({ signed_by_name: "", signed_by_role: "", signed_by_email: "" });

  const loadEvents = async () => {
    try {
      const r = await api.get(`/integration/psdoc/opportunities/${opportunityId}/events`);
      setEvents(r.data);
    } catch { /* ignore */ }
  };

  React.useEffect(() => {
    if (expanded && events.length === 0) loadEvents();
    // eslint-disable-next-line
  }, [expanded]);

  const signingUrl = project.signing_token ? `${window.location.origin}/sign/${project.signing_token}` : null;

  const issueReviewLink = async () => {
    setIssuing(true);
    try {
      await api.post(`/projects/${opportunityId}/issue-signing-link`);
      toast.success("Review link generated — copy it and email via Outlook");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue review link");
    } finally {
      setIssuing(false);
    }
  };

  const copyLink = () => {
    if (!signingUrl) return;
    navigator.clipboard.writeText(signingUrl);
    toast.success("Copied to clipboard");
  };

  const markSentForSigning = async () => {
    if (!window.confirm("Confirm: you've emailed the SOW + Master Agreement package to the client via Outlook?\n\nThis flips the status to Out for Signing and notifies PSDoc.")) return;
    setMarking(true);
    try {
      const r = await api.post(`/projects/${opportunityId}/mark-out-for-signing`);
      const push = r.data?.psdoc_push || {};
      if (push.ok && !push.skipped) toast.success("Marked as sent — PSDoc updated");
      else if (push.skipped) toast.warning("Marked as sent — PSDoc not configured");
      else toast.warning(`Marked as sent — PSDoc mirror failed: ${push.error || "unknown"}`);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark as sent");
    } finally {
      setMarking(false);
    }
  };

  const submitMarkSigned = async () => {
    setMarking(true);
    try {
      const r = await api.post(`/projects/${opportunityId}/mark-signed`, signForm);
      toast.success("Signed — moved to Closed Won + notifying PSDoc");
      setSigningConfirmOpen(false);
      setSignForm({ signed_by_name: "", signed_by_role: "", signed_by_email: "" });
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark signed");
    } finally {
      setMarking(false);
    }
  };

  const failed = ["send_failed", "won_failed", "config_failed"].includes(status);
  const notConfigured = ["skipped_not_configured", "won_skipped_not_configured"].includes(status);
  // Brown-bench sanity check — if the deal was flagged brown at discovery, the
  // SOW pack MUST contain a rollback_plan + cutover_checklist. Otherwise show
  // a red banner so the admin holds shipment until PSDoc regenerates the pack.
  const bench = project.discovery_bench || (project.client_doc && project.client_doc.discovery_bench) || "";
  const brownExtrasMissing = bench === "brown" && status === "sow_ready"
    && !(sections?.rollback_plan?.length > 0)
    && !(sections?.cutover_checklist?.length > 0);
  // 3-phase signing UI — starts at sow_ready, ends when signed_at is set
  const signingPhase = (() => {
    if (project.signed_at) return "signed";
    if (["sow_out_for_signing", "sow_viewed_by_client"].includes(status)) return "awaiting_signed_copies";
    if (status === "sow_ready" && !project.signing_token) return "generate_link";
    if (status === "sow_ready" && project.signing_token) return "ready_to_send";
    return null;
  })();

  return (
    <div className="col-span-2 border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="psdoc-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PaperPlaneTilt size={16} className="text-blue-600" />
          <span className="text-sm font-medium text-[#0F1E38]">PSDoc integration</span>
          {status ? (
            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${psdocBadgeClass(status)}`}>
              {psdocLabel(status)}
            </span>
          ) : (
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
              not sent yet
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
              data-testid="psdoc-view-link"
            >
              View in PSDoc ↗
            </a>
          )}
          {(failed || notConfigured) && (
            <Button size="sm" variant="outline" onClick={onRetry} data-testid="psdoc-retry-btn">
              <ArrowsClockwise size={12} className="mr-1" /> Retry handoff
            </Button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-slate-600 hover:text-[#0F1E38]"
            data-testid="psdoc-expand-btn"
          >
            {expanded ? "Hide" : "Details"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {brownExtrasMissing && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="brown-extras-missing">
          ⚠ This deal is <b>brown-field</b> but the SOW pack from PSDoc doesn't include a <b>Rollback Plan</b> or <b>Cutover Checklist</b>. Ask PSDoc to regenerate before sending to the client.
        </div>
      )}

      {/* ---- 3-phase SOW signing block ---- */}
      {signingPhase === "generate_link" && (
        <div className="mt-3 border border-emerald-300 bg-emerald-50/60 rounded p-3 space-y-2" data-testid="signing-block-generate">
          <div className="text-xs font-medium text-emerald-800">✓ PSDoc-approved SOW ready</div>
          <div className="text-xs text-slate-700">
            Generate a review link, then email it to the client with the SOW + Master Agreement attachments via Outlook.
          </div>
          <Button size="sm" onClick={issueReviewLink} disabled={issuing} data-testid="issue-review-link-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {issuing ? "Generating…" : "Generate Review Link"}
          </Button>
        </div>
      )}

      {signingPhase === "ready_to_send" && (
        <div className="mt-3 border border-blue-300 bg-blue-50/60 rounded p-3 space-y-2" data-testid="signing-block-ready-to-send">
          <div className="text-xs font-medium text-blue-800">Review link ready — email the full package via Outlook</div>
          <div className="flex items-center gap-2">
            <input readOnly value={signingUrl}
              className="flex-1 text-xs font-mono bg-white border border-slate-300 rounded px-2 py-1"
              onClick={(e) => e.target.select()}
              data-testid="signing-link-url"
            />
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="signing-copy-btn">Copy Link</Button>
            <a href={signingUrl} target="_blank" rel="noreferrer" data-testid="signing-open-print" className="text-xs px-3 py-1 rounded border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium">
              Open · Print → PDF ↗
            </a>
          </div>
          <div className="text-[10px] text-slate-500">
            Open the link, then use your browser's Print → <b>Save as PDF</b> to attach the SOW to your Outlook email alongside the Master Agreement.
            Link expires {project.signing_expires_at ? new Date(project.signing_expires_at).toLocaleString() : "in 14 days"} — no auto-email is sent.
          </div>
          <Button size="sm" onClick={markSentForSigning} disabled={marking} data-testid="mark-sent-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {marking ? "Marking…" : "Mark Sent for Signing"}
          </Button>
        </div>
      )}

      {signingPhase === "awaiting_signed_copies" && (
        <div className="mt-3 border border-violet-300 bg-violet-50/60 rounded p-3 space-y-2" data-testid="signing-block-awaiting">
          <div className="text-xs font-medium text-violet-800 flex items-center gap-1.5">
            {status === "sow_viewed_by_client" ? "✓ Client has viewed the SOW" : "SOW package sent — awaiting signed copies"}
          </div>
          <div className="text-xs text-slate-700">
            Once the client returns the physically signed SOW + Master Agreement via email, click below to close the deal.
            {project.sow_out_for_signing_at && (
              <span className="block mt-1 text-[10px] font-mono text-slate-500">
                Sent {new Date(project.sow_out_for_signing_at).toLocaleString()}
                {project.sow_viewed_by_client_at && ` · Viewed ${new Date(project.sow_viewed_by_client_at).toLocaleString()}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setSigningConfirmOpen(true)} data-testid="mark-signed-btn" className="bg-violet-600 hover:bg-violet-700 text-white">
              Mark Signed
            </Button>
            {signingUrl && (
              <a href={signingUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Re-open review link ↗</a>
            )}
          </div>
        </div>
      )}

      {signingPhase === "signed" && (
        <div className="mt-3 border border-emerald-300 bg-emerald-50/60 rounded p-3" data-testid="signing-block-signed">
          <div className="text-xs font-medium text-emerald-800">
            ✓ Signed on {new Date(project.signed_at).toLocaleString()}
            {project.signed_by_name && <span className="ml-1 text-slate-700">by {project.signed_by_name}{project.signed_by_role && ` (${project.signed_by_role})`}</span>}
          </div>
        </div>
      )}

      {/* Mark-Signed confirmation dialog */}
      <Dialog open={signingConfirmOpen} onOpenChange={setSigningConfirmOpen}>
        <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-md" data-testid="mark-signed-dialog">
          <DialogHeader><DialogTitle>Mark as Signed</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              This closes the deal (→ Closed Won) and notifies PSDoc to start config generation. Only click if you have the physically-signed SOW + Master Agreement in hand.
            </div>
            <div>
              <Label className="text-xs">Signer name (optional)</Label>
              <Input value={signForm.signed_by_name} onChange={(e) => setSignForm({ ...signForm, signed_by_name: e.target.value })} placeholder="e.g. Sarah Kelly" data-testid="signed-name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Signer role (optional)</Label>
              <Input value={signForm.signed_by_role} onChange={(e) => setSignForm({ ...signForm, signed_by_role: e.target.value })} placeholder="e.g. CTO" data-testid="signed-role" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Signer email (optional)</Label>
              <Input type="email" value={signForm.signed_by_email} onChange={(e) => setSignForm({ ...signForm, signed_by_email: e.target.value })} data-testid="signed-email" className="mt-1 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigningConfirmOpen(false)} className="border-slate-300">Cancel</Button>
            <Button onClick={submitMarkSigned} disabled={marking} data-testid="mark-signed-submit" className="bg-violet-600 hover:bg-violet-700 text-white">
              {marking ? "Recording…" : "Confirm — SOW Signed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {notConfigured && (
        <div className="mt-2 text-xs text-slate-600">
          Set <code className="text-[11px] bg-white border border-slate-200 rounded px-1">PSDOC_BASE_URL</code> in <code className="text-[11px] bg-white border border-slate-200 rounded px-1">backend/.env</code> to enable live handoff.
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-3">
          {attachments && attachments.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1.5">Attachments from PSDoc</div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:border-blue-400 hover:text-blue-700 flex items-center gap-1"
                  >
                    <FileArrowDown size={12} /> {a.kind?.toUpperCase() || "DOC"} {a.filename ? `· ${a.filename}` : ""}
                  </a>
                ))}
              </div>
            </div>
          )}

          {sections?.solution_overview && (
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Solution overview</div>
              <div className="text-xs text-slate-600 whitespace-pre-wrap bg-white border border-slate-200 rounded p-2 max-h-32 overflow-y-auto">
                {sections.solution_overview}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-medium text-slate-700 mb-1.5">Event log</div>
            <div className="bg-white border border-slate-200 rounded max-h-48 overflow-y-auto">
              {events.length === 0 ? (
                <div className="p-2 text-xs text-slate-400 text-center">No events yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {events.map((ev) => (
                      <tr key={ev.id} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-1 font-mono text-slate-500 whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</td>
                        <td className="px-2 py-1">
                          <span className={`text-[10px] font-mono uppercase px-1 rounded ${ev.source === "inbound" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{ev.source}</span>
                        </td>
                        <td className="px-2 py-1 font-mono">{ev.kind}</td>
                        <td className="px-2 py-1">
                          {ev.ok ? <span className="text-emerald-600">ok</span> : <span className="text-red-600" title={ev.error || ""}>failed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
